/**
 * Sprint 23.7 — onboarding-completed flag.
 *
 * Persisted in `workspace_context` under `onboarding_completed`. Drives the
 * `/welcome` route guard and the index redirect: a workspace that has finished
 * (or skipped) onboarding never sees the welcome flow again (Q6). Existing
 * workspaces are additionally protected by the "has agents" check at the call
 * sites — they never see `/welcome` even before the flag is set.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { useAuth } from "../../lib/hooks/use-auth";

const ONBOARDING_KEY = "onboarding_completed";

export function onboardingQueryKey(workspaceId: string | null) {
  return ["onboarding-completed", workspaceId] as const;
}

export interface UseOnboardingResult {
  completed: boolean | undefined;
  isLoading: boolean;
  markComplete: () => Promise<void>;
}

export function useOnboardingComplete(): UseOnboardingResult {
  const { currentWorkspaceId } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: onboardingQueryKey(currentWorkspaceId),
    queryFn: async (): Promise<boolean> => {
      const v = await mcpClient.getWorkspaceContext(ONBOARDING_KEY);
      return v === "1" || v === "true";
    },
    enabled: !!currentWorkspaceId,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: () => mcpClient.setWorkspaceContext(ONBOARDING_KEY, "1"),
    onSuccess: () => qc.setQueryData(onboardingQueryKey(currentWorkspaceId), true),
  });

  return {
    completed: query.data,
    isLoading: query.isLoading,
    markComplete: () => mutation.mutateAsync(),
  };
}
